import {
  Link,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import poppins400 from "@fontsource/poppins/files/poppins-latin-400-normal.woff2?url";
import poppins600 from "@fontsource/poppins/files/poppins-latin-600-normal.woff2?url";
import poppins700 from "@fontsource/poppins/files/poppins-latin-700-normal.woff2?url";

import appCss from "../styles.css?url";
import { ThemeModeProvider } from "@/theme/ThemeModeProvider";
import { AuthProvider } from "@/hooks/useAuth";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Toaster } from "sonner";
import { useThemeMode } from "@/theme/ThemeModeProvider";
import { InstallAppPrompt } from "@/components/InstallAppPrompt";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Box sx={{ textAlign: "center", maxWidth: 480 }}>
        <Typography variant="h1" sx={{ fontSize: 96, fontWeight: 800 }}>
          404
        </Typography>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Page not found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          The page you're looking for doesn't exist or has been moved.
        </Typography>
        <Button component={Link} to="/" variant="contained">
          Go home
        </Button>
      </Box>
    </Box>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "JobYoda — AI-native Career Progression" },
      {
        name: "description",
        content:
          "AI-native career progression: profile ingestion, job success scoring, tailored materials, career path mapping, and rejection learning.",
      },
      { name: "author", content: "JobYoda" },
      { property: "og:title", content: "JobYoda — AI-native Career Progression" },
      {
        property: "og:description",
        content:
          "Score yourself against any job description, generate tailored materials, and map your career path with AI.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "JobYoda — AI-native Career Progression" },
      { name: "description", content: "An AI-powered SaaS platform that matches candidates to jobs, automates applications, and maps career paths." },
      { property: "og:description", content: "An AI-powered SaaS platform that matches candidates to jobs, automates applications, and maps career paths." },
      { name: "twitter:description", content: "An AI-powered SaaS platform that matches candidates to jobs, automates applications, and maps career paths." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/b73c9b5a-55e4-41be-8bfe-7517ef00245c/id-preview-0f643fc4--e5bf3995-82ec-4f36-a515-2502da8ffa41.lovable.app-1776541655582.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/b73c9b5a-55e4-41be-8bfe-7517ef00245c/id-preview-0f643fc4--e5bf3995-82ec-4f36-a515-2502da8ffa41.lovable.app-1776541655582.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      {
        rel: "preload",
        href: poppins400,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: poppins600,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: poppins700,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
  }),
  shellComponent: RootShell,
  notFoundComponent: NotFoundComponent,
});

function RootInner({ children }: { children: React.ReactNode }) {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <AuthProvider>
          <IconGradientDefs />
          <PaymentTestModeBanner />
          {children}
          <ToasterWithMode />
          <InstallAppPrompt />
        </AuthProvider>
      </ThemeModeProvider>
    </QueryClientProvider>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style
          // Inlined @font-face rules so font requests start as soon as the HTML
          // is parsed — avoids the HTML → CSS → font request chain.
          dangerouslySetInnerHTML={{
            __html: `@font-face{font-family:"Poppins";font-style:normal;font-display:swap;font-weight:400;src:url("${poppins400}") format("woff2");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}@font-face{font-family:"Poppins";font-style:normal;font-display:swap;font-weight:600;src:url("${poppins600}") format("woff2");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}@font-face{font-family:"Poppins";font-style:normal;font-display:swap;font-weight:700;src:url("${poppins700}") format("woff2");unicode-range:U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD}`,
          }}
        />
      </head>
      <body>
        <RootInner>{children}</RootInner>
        <Scripts />
      </body>
    </html>
  );
}

function IconGradientDefs() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute" }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="jobyoda-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D7E89B" />
          <stop offset="35%" stopColor="#A6D785" />
          <stop offset="70%" stopColor="#7BB661" />
          <stop offset="100%" stopColor="#4F8A3A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function ToasterWithMode() {
  const { mode } = useThemeMode();
  return (
    <Toaster
      position="bottom-center"
      richColors
      closeButton
      theme={mode === "dark" ? "dark" : "light"}
    />
  );
}


